import React from "react";

/**
 * Asked only when a long invoice will not fit one sheet.
 *
 * The layout compresses itself to fit — closing the gap under the letterhead
 * before it gives up a page — and for an invoice a line or two over that is
 * exactly right. For one that is genuinely two pages of work it is not: the
 * person issuing it should decide whether the client receives a squeezed sheet
 * or two readable ones.
 *
 * Most invoices never see this. It appears when, and only when, there is a
 * real decision to make.
 */

// A few pixels over is the layout settling, not a second page.
const SETTLING_SLACK_PX = 8;

/** Whether the printed document would run past a single sheet. */
export function wouldOverflow({ contentHeight, sheetHeight }) {
  if (!(contentHeight > 0) || !(sheetHeight > 0)) return false;
  return contentHeight > sheetHeight + SETTLING_SLACK_PX;
}

/** Sheets the invoice would take if allowed to run on. */
export function pagesNeeded({ contentHeight, sheetHeight }) {
  if (!(contentHeight > 0) || !(sheetHeight > 0)) return 1;
  return Math.max(1, Math.ceil(contentHeight / sheetHeight));
}

// Letter at 96dpi with the 0.5in margins the invoice prints with. Computed,
// not measured: the on-screen preview is a different width and a different
// height from the sheet, and measuring IT to predict the PRINTED page is how
// an invoice that "fits" on screen still spills.
export const PRINT_SHEET_WIDTH_PX = 7.5 * 96;   // 720
export const PRINT_SHEET_HEIGHT_PX = 10 * 96;   // 960

/**
 * How tall the invoice would be if printed, by laying a copy of it out at the
 * sheet's exact width off-screen.
 *
 * Returns zeroes where there is nothing to measure (no node, or a DOM with no
 * layout, as under jsdom). wouldOverflow() reads that as "fits", so the
 * question is never asked on a guess.
 */
export function measurePrintLayout(node) {
  if (!node || typeof document === "undefined") {
    return { contentHeight: 0, sheetHeight: PRINT_SHEET_HEIGHT_PX };
  }
  const probe = document.createElement("div");
  probe.setAttribute("aria-hidden", "true");
  probe.style.cssText = [
    "position:absolute",
    "left:-10000px",
    "top:0",
    `width:${PRINT_SHEET_WIDTH_PX}px`,
    "height:auto",
    "visibility:hidden",
    "pointer-events:none",
  ].join(";");

  const copy = node.cloneNode(true);
  // The original is a one-sheet-tall flex column; the copy has to be free to
  // take whatever height the content actually needs.
  copy.style.height = "auto";
  copy.style.minHeight = "0";
  copy.style.display = "block";
  // The on-screen card's padding and border are not printed -- the sheet's
  // own 0.5in margin stands in for them (see printSheetCss).
  copy.style.padding = "0";
  copy.style.border = "0";
  copy.style.maxWidth = "none";
  copy.style.boxSizing = "border-box";
  // Under height:auto a flex item takes its full basis, so the gap under the
  // letterhead and the one above the footer would count at full size. On the
  // sheet they close before anything spills, so measure them closed.
  copy
    .querySelectorAll('[data-testid="doc-letterhead-gap"], [data-testid="doc-spacer"]')
    .forEach((gap) => {
      gap.style.flex = "0 0 0px";
    });
  // Never printed, so never counted.
  copy
    .querySelectorAll(".screen-only, #preview-sash, #print-button, #edit-button")
    .forEach((el) => el.remove());
  copy.removeAttribute("id");
  probe.appendChild(copy);
  document.body.appendChild(probe);
  let contentHeight = 0;
  try {
    contentHeight = probe.scrollHeight || 0;
  } finally {
    document.body.removeChild(probe);
  }
  return { contentHeight, sheetHeight: PRINT_SHEET_HEIGHT_PX };
}

/**
 * How far to shrink the invoice so it fits one sheet: 1 when it already fits.
 *
 * Measured with both gaps closed, so a fitted invoice starts right under the
 * letterhead and its footer still lands on the foot of the sheet. A hair under
 * the exact ratio, because a fitted page that is one pixel over prints a
 * second, blank sheet.
 */
export function fitScale({ contentHeight, sheetHeight }) {
  if (!(contentHeight > 0) || !(sheetHeight > 0)) return 1;
  if (contentHeight <= sheetHeight) return 1;
  return Math.floor((sheetHeight / contentHeight) * 0.99 * 1000) / 1000;
}

/**
 * The print stylesheet for an invoice: one letter sheet, nothing else on it.
 *
 * `selector` names the element holding the InvoiceDocument. Everything that
 * does not contain it is display:none -- NOT visibility:hidden, which hid the
 * app's menus and toolbar but left them taking up room, and they printed as a
 * blank second sheet behind every invoice. Its ancestors are flattened to
 * plain one-sheet-tall blocks so the sheet height reaches it as 100%.
 *
 * 100%, never 100vh: under the print dialog's Scale the viewport height does
 * not follow the page, and a 100vh sheet left the footer floating a fifth of
 * the way up. A max-width left over from the screen card (800px) also capped
 * the width, which is what squeezed a scaled invoice into the left of the page.
 *
 * mode "fit" zooms the WHOLE document by `scale`, so the type shrinks but the
 * layout still spans the full width of the sheet, and the gaps above the date
 * and below the totals are what absorb any slack. "flow" lets it run on.
 */
export function printSheetCss(selector, { mode = "fit", scale = 1 } = {}) {
  const sheet = mode === "flow"
    ? "height: auto !important; min-height: 100% !important;"
    // zoom, and NOT width/height divided by the scale: a zoomed element's
    // percentage sizes are already in zoomed units, so 100% is the sheet.
    : `height: 100% !important; min-height: 0 !important; zoom: ${scale};`;
  return `
    @page { size: letter; margin: 0.5in; }
    @media print {
      html, body {
        height: 100% !important;
        margin: 0 !important;
        padding: 0 !important;
        background: white !important;
      }
      body *:not(:has(${selector})):not(${selector}):not(${selector} *) {
        display: none !important;
      }
      body *:has(${selector}) {
        display: block !important;
        position: static !important;
        margin: 0 !important;
        padding: 0 !important;
        border: 0 !important;
        width: auto !important;
        min-width: 0 !important;
        max-width: none !important;
        height: 100% !important;
        min-height: 0 !important;
        transform: none !important;
      }
      ${selector} {
        position: static !important;
        margin: 0 !important;
        padding: 0 !important;
        border: none !important;
        border-radius: 0 !important;
        box-shadow: none !important;
        transform: none !important;
        background: white !important;
        box-sizing: border-box !important;
        width: 100% !important;
        max-width: none !important;
        display: flex !important;
        flex-direction: column !important;
        ${sheet}
      }
    }
  `;
}

// Past this, "fit on one page" stops being a sensible thing to hand a client.
const UNREADABLE_SQUEEZE_PAGES = 3;

const BUTTON = {
  padding: "9px 16px",
  borderRadius: 6,
  fontSize: 13,
  cursor: "pointer",
  fontWeight: 600,
};

export default function PageBreakChoice({ open, pagesIfFlowed, onChoose, onCancel }) {
  if (!open) return null;
  const pages = Math.max(2, Number(pagesIfFlowed) || 2);
  const bigSqueeze = pages >= UNREADABLE_SQUEEZE_PAGES;

  return (
    <div
      role="dialog"
      aria-modal="true"
      aria-label="This invoice is longer than one page"
      style={{
        position: "fixed",
        inset: 0,
        background: "rgba(17,24,39,0.45)",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        zIndex: 1000,
        padding: 16,
      }}
    >
      <div
        style={{
          background: "white",
          borderRadius: 10,
          padding: 24,
          maxWidth: 460,
          width: "100%",
          boxShadow: "0 10px 40px rgba(0,0,0,0.25)",
        }}
      >
        <h3 style={{ margin: "0 0 8px", fontSize: 17 }}>
          This invoice is longer than one page
        </h3>
        <p style={{ margin: "0 0 18px", color: "#4b5563", fontSize: 14, lineHeight: 1.5 }}>
          It needs <strong>{pages} pages</strong> at the firm&rsquo;s text size.
          You can let it run on, or squeeze it onto a single sheet.
        </p>

        {bigSqueeze && (
          <p
            data-testid="squeeze-warning"
            style={{
              margin: "0 0 18px",
              padding: "10px 12px",
              background: "#fffbeb",
              border: "1px solid #fde68a",
              borderRadius: 6,
              color: "#92400e",
              fontSize: 13,
              lineHeight: 1.5,
            }}
          >
            Fitting {pages} pages onto one will make this hard to read. Printing
            it on {pages} pages is usually the better answer.
          </p>
        )}

        <div style={{ display: "flex", gap: 10, justifyContent: "flex-end", flexWrap: "wrap" }}>
          <button
            type="button"
            onClick={onCancel}
            style={{ ...BUTTON, border: "1px solid #d1d5db", background: "white", fontWeight: 400 }}
          >
            Cancel
          </button>
          <button
            type="button"
            onClick={() => onChoose("fit")}
            style={{ ...BUTTON, border: "1px solid #d1d5db", background: "#f9fafb" }}
          >
            Fit on one page
          </button>
          <button
            type="button"
            onClick={() => onChoose("flow")}
            style={{ ...BUTTON, border: "none", background: "#111827", color: "white" }}
          >
            Print on {pages} pages
          </button>
        </div>
      </div>
    </div>
  );
}
